<template>
  <div class="flex w-full flex-col">
    <div v-if="title" class="text-xl font-semibold">{{ title }}</div>
    <component
      :is="Comp"
      :nodeType="nodeType"
      :module="currentModule"
      :key="currentModule?.code || props.dataType"
      @update="(payload) => updateModuleInStore(payload)"></component>
  </div>
</template>

<script setup>
import { useDataValidation } from '@/composables/useDataValidation';
import { SETTING_PANEL_TABS } from '@/constants/settingPanels';
import { useCruxModuleStore } from '@/stores/api/cruxmodules.js';
import { useLedPanelStore } from '@/stores/api/ledpanel';
import { useProjectorStore } from '@/stores/api/projector';
import { useAppStore } from '@/stores/appStore';
import { useSelectionStore } from '@/stores/ui/selection.js';
import { useTimelinePlayerStore } from '@/stores/ui/timelinePlayer';
import { getObjectByCode } from '@/threejs/objectManager';
import { useConversions } from '@/utils/useConversions.js';
import { computed } from 'vue';
import SelectedNodeComponent from './SelectedNodeComponent.vue';
import Settings from './Settings.vue';

const { average } = useConversions();

const props = defineProps({
  dataType: {
    type: String,
    default: 'selectedNode',
  },
  nodeType: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: null,
  },
});

const cruxModuleStore = useCruxModuleStore();
const selectionStore = useSelectionStore();
const appStore = useAppStore();
const { sanitizeModuleData } = useDataValidation();
const projectorStore = useProjectorStore();
const ledPanelStore = useLedPanelStore();
const timelinePlayerStore = useTimelinePlayerStore();

const currentModule = computed(() => {
  switch (props.dataType) {
    case SETTING_PANEL_TABS.PROJECT_SETTINGS:
      return cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.PROJECT_SETTINGS);
    case SETTING_PANEL_TABS.VIEWPORT_SETTINGS:
      return cruxModuleStore.getModuleByType(SETTING_PANEL_TABS.VIEWPORT_SETTINGS);
    case 'selectedNode':
      // Find the crux module that matches the selected object ID
      if (selectionStore.selectedObjectId) {
        return cruxModuleStore.cruxModules.find((module) => module.code === selectionStore.selectedObjectId);
      }
      return null;
    default:
      return null;
  }
});

const Comp = computed(() => {
  switch (props.dataType) {
    case 'project_settings':
    case 'viewport_settings':
      return Settings;
    case 'selectedNode':
      return SelectedNodeComponent;
    default:
      return null;
  }
});

const updateModuleInStore = (payload) => {
  appStore.setDisplayText('Auto Saving...');
  if (props.dataType === 'selectedNode' && selectionStore.selectedObjectId) {
    // For selected nodes, update the specific module by its code
    const module = cruxModuleStore.cruxModules.find((m) => m.code === selectionStore.selectedObjectId);
    if (module) {
      if (payload.isRoot === true) {
        // Update root property directly
        const updatedModule = sanitizeModuleData({
          ...module,
          [payload.key]: payload.value,
        });

        cruxModuleStore.update(module.code, updatedModule);
      } else if (payload.key === 'libraryCode') {
        let result;
        if (module.type === 'projector') {
          result = projectorStore.getProjectorDataByCode(payload.value);
        } else {
          result = ledPanelStore.getLedPanelDataByCode(payload.value);
        }
        if (result) {
          const { code, ...libraryData } = result;
          const updatedModule = {
            ...module,
            data: {
              ...module.data,
              ...libraryData,
              libraryCode: payload.value,
            },
          };
          cruxModuleStore.update(module.code, updatedModule);
        } else {
          console.warn('No projector or LED panel found with code:', payload.value);
        }
      } else {
        // convert key from position.x to nested object
        const keys = payload.key.split('.');
        let updatedData = { ...module.data };
        let current = updatedData;
        if (payload.key === 'lock_scale') {
          const mesh = getObjectByCode(module.code);
          if (mesh) {
            mesh.userData.lock_scale = payload.value;
          }
          if (payload.value === false && typeof current.scale === 'number') {
            current.scale = {
              x: current.scale,
              y: current.scale,
              z: current.scale,
            };
          } else if (payload.value === true && typeof current.scale === 'object') {
            const uniformScale = average([current.scale.x ?? 1, current.scale.y ?? 1, current.scale.z ?? 1]);
            current.scale = uniformScale;
          }
        }
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          if (!(key in current)) {
            current[key] = {};
          }
          current = current[key];
        }
        current[keys[keys.length - 1]] = payload.value;

        // For groups: move child projector targets when group position changes
        if (module.type === 'group' && payload.key.startsWith('position.')) {
          const axis = keys[keys.length - 1];
          const oldVal = module.data.position?.[axis] ?? 0;
          const delta = payload.value - oldVal;
          if (delta !== 0) {
            const children = cruxModuleStore.cruxModules.filter(
              (m) => m.data?.parentCode === module.code && m.type === 'projector' && m.data?.target
            );
            children.forEach((child) => {
              const projectorObj = getObjectByCode(child.code);
              if (projectorObj) {
                const newTarget = projectorObj.userData.target.clone();
                newTarget[axis] += delta;
                projectorObj.updateTarget(newTarget);
              }
              child.data.target = { ...child.data.target };
              child.data.target[axis] = (child.data.target[axis] ?? 0) + delta;
            });
          }
        }
        // When a new source asset is assigned to a projector/LED, initialize media block defaults and reset timeline
        if (
          payload.key === 'source' &&
          payload.value &&
          (module.type === 'projector' || module.type === 'led_screen')
        ) {
          if (!updatedData.mediaStartTime && updatedData.mediaStartTime !== 0) {
            updatedData.mediaStartTime = 0;
          }
          if (!updatedData.mediaDuration) {
            updatedData.mediaDuration = 10;
          }
          if (!updatedData.mediaStart && updatedData.mediaStart !== 0) {
            updatedData.mediaStart = 0;
          }
          timelinePlayerStore.setCurrentTime(0);
        }

        const updatedModule = sanitizeModuleData({
          ...module,
          data: updatedData,
        });

        cruxModuleStore.update(module.code, updatedModule);
      }
    }
  } else {
    // For other data types (project_settings, viewport_settings)
    cruxModuleStore.updateModuleData(props.dataType, payload.key, payload.value);
  }
};
</script>
